import { useQuery, useMutation } from '@apollo/react-hooks';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  CREATE_TOPIC,
  UPDATE_TOPIC,
  DELETE_TOPIC,
  CREATE_VIDEO,
  UPDATE_VIDEO,
  DELETE_VIDEO,
  UPDATE_SETTINGS,
} from './mutations';
import {
  GET_ADMIN_TOPICS,
  GET_ADMIN_VIDEOS,
  GET_ADMIN_SETTINGS,
} from './queries';

// --- Topic Hooks ---

export function useGetAdminTopics(mode) {
  const variables = mode ? { mode } : {};
  const result = useQuery(GET_ADMIN_TOPICS, { variables });
  const topics = result.data?.getLearningHubAdminTopics || [];
  return { ...result, topics };
}

export function useCreateTopic() {
  const [mutation] = useMutation(CREATE_TOPIC, {
    refetchQueries: [{ query: GET_ADMIN_TOPICS }],
  });
  return useCallback(
    async (input) => {
      try {
        const response = await mutation({ variables: { input } });
        toast.success('Topic created');
        return response.data.createLearningHubTopic;
      } catch (error) {
        toast.error(error.message || 'Failed to create topic');
        return null;
      }
    },
    [mutation]
  );
}

export function useUpdateTopic() {
  const [mutation] = useMutation(UPDATE_TOPIC, {
    refetchQueries: [{ query: GET_ADMIN_TOPICS }],
  });
  return useCallback(
    async (input) => {
      try {
        const response = await mutation({ variables: { input } });
        toast.success('Topic updated');
        return response.data.updateLearningHubTopic;
      } catch (error) {
        toast.error(error.message || 'Failed to update topic');
        return null;
      }
    },
    [mutation]
  );
}

export function useDeleteTopic() {
  const [mutation] = useMutation(DELETE_TOPIC, {
    refetchQueries: [{ query: GET_ADMIN_TOPICS }],
  });
  return useCallback(
    async (id) => {
      try {
        await mutation({ variables: { id } });
        toast.success('Topic deleted');
        return true;
      } catch (error) {
        toast.error(error.message || 'Failed to delete topic');
        return false;
      }
    },
    [mutation]
  );
}

// --- Video Hooks ---

export function useGetAdminVideos(input) {
  const result = useQuery(GET_ADMIN_VIDEOS, {
    variables: { input: input || {} },
  });
  const videos = result.data?.getLearningHubAdminVideos?.results || [];
  const count = result.data?.getLearningHubAdminVideos?.count || 0;
  return { ...result, videos, count };
}

export function useCreateVideo() {
  const [mutation] = useMutation(CREATE_VIDEO, {
    refetchQueries: [{ query: GET_ADMIN_VIDEOS, variables: { input: {} } }],
  });
  return useCallback(
    async (input) => {
      try {
        const response = await mutation({ variables: { input } });
        toast.success('Video created');
        return response.data.createLearningHubVideo;
      } catch (error) {
        toast.error(error.message || 'Failed to create video');
        return null;
      }
    },
    [mutation]
  );
}

export function useUpdateVideo() {
  const [mutation] = useMutation(UPDATE_VIDEO, {
    refetchQueries: [{ query: GET_ADMIN_VIDEOS, variables: { input: {} } }],
  });
  return useCallback(
    async (input) => {
      try {
        const response = await mutation({ variables: { input } });
        toast.success('Video updated');
        return response.data.updateLearningHubVideo;
      } catch (error) {
        toast.error(error.message || 'Failed to update video');
        return null;
      }
    },
    [mutation]
  );
}

export function useDeleteVideo() {
  const [mutation] = useMutation(DELETE_VIDEO, {
    refetchQueries: [{ query: GET_ADMIN_VIDEOS, variables: { input: {} } }],
  });
  return useCallback(
    async (id) => {
      try {
        await mutation({ variables: { id } });
        toast.success('Video deleted');
        return true;
      } catch (error) {
        toast.error(error.message || 'Failed to delete video');
        return false;
      }
    },
    [mutation]
  );
}

// --- Settings Hooks ---

export function useGetAdminSettings() {
  const result = useQuery(GET_ADMIN_SETTINGS);
  const settings = result.data?.getLearningHubSettings || null;
  return { ...result, settings };
}

export function useUpdateSettings() {
  const [mutation] = useMutation(UPDATE_SETTINGS);
  return useCallback(
    async (input) => {
      try {
        const response = await mutation({ variables: { input } });
        toast.success('Settings saved');
        return response.data.updateLearningHubSettings;
      } catch (error) {
        toast.error(error.message || 'Failed to save settings');
        return null;
      }
    },
    [mutation]
  );
}
